import { useState } from "react";

export default function BuiltForTitleSection() {
  const [activeTab, setActiveTab] = useState<"agents" | "lenders">("agents");

  const tabs = [
    { id: "agents" as const, label: "Listing Agents", icon: "ri-home-4-line" },
    { id: "lenders" as const, label: "Loan Officers", icon: "ri-bank-line" },
  ];

  const content = {
    agents: {
      title: "Be the first title company every listing agent hears from — before they've chosen who they're working with.",
      bullets: [
        { icon: "ri-calendar-check-line", label: "Reach agents the same day of a new listing" },
        { icon: "ri-speed-up-line", label: "Start conversations before competitors are even aware" },
        { icon: "ri-repeat-line", label: "Stay top of mind with automatic follow-up" },
        { icon: "ri-arrow-right-up-line", label: "Convert agent responses directly into closings" },
      ],
      script: "\"Hey, this is Mia calling on behalf of [Your Title Company]. I saw the new listing just hit on Maple Street — congrats! We're handling the seller side on a few properties in that area right now. How do you usually handle title on your closings?\"",
    },
    lenders: {
      title: "Stay top-of-mind with every lender",
      bullets: [
        { icon: "ri-timer-flash-line", label: "Triggered in seconds" },
        { icon: "ri-user-voice-line", label: "Human-sounding AI voice" },
        { icon: "ri-repeat-line", label: "Follows up automatically" },
        { icon: "ri-arrow-right-up-line", label: "Routes warm leads to you" },
      ],
      script: "\"Hi, I'm reaching out from [Your Title Company]. We've been working with some mutual agent contacts in your area and wanted to make sure you knew who to call when your next closing needs title services — we close fast and make your clients happy.\"",
    },
  };

  const c = content[activeTab];

  return (
    <section className="bg-alt-bg py-14 md:py-24 px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-8 md:mb-10">
          <div className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-1.5 mb-6">
            <i className="ri-building-2-line text-navy text-xs" />
            <span className="text-xs font-semibold text-navy">Built for Title</span>
          </div>
          <h2
            className="text-navy font-semibold"
            style={{ fontSize: "clamp(26px, 3vw, 38px)", lineHeight: "1.2" }}
          >
            This Isn&apos;t Cold Calling —{" "}
            <span style={{ color: "#f1663a" }}>It&apos;s Winning the Closing Before It Starts</span>
          </h2>
        </div>

        {/* Tabs with label */}
        <div className="flex flex-col items-center gap-3 mb-8 md:mb-12">
          <p className="text-[#4a5568] text-sm font-medium">Choose who you want to win with — listing agents or loan officers:</p>
          <div className="flex items-center gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === tab.id
                    ? "bg-navy text-white"
                    : "bg-white border border-gray-200 text-[#4a5568] hover:border-navy/30"
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`${tab.icon} text-xs`} />
                </span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-center">
          <div>
            <h3 className="text-navy font-semibold text-xl mb-5 leading-snug">{c.title}</h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {c.bullets.map((item) => (
                <div key={item.label} className="flex items-start gap-3">
                  <span className="w-8 h-8 flex items-center justify-center bg-orange-50 rounded-lg shrink-0 mt-0.5">
                    <i className={`${item.icon} text-primary text-sm`} />
                  </span>
                  <span className="text-navy text-sm font-medium leading-snug">{item.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Script card */}
          <div className="bg-white rounded-2xl p-5 md:p-7 border border-gray-100 relative">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-8 h-8 flex items-center justify-center bg-orange-50 rounded-lg">
                <i className="ri-mic-2-line text-primary text-sm" />
              </div>
              <div>
                <p className="text-navy font-semibold text-xs">Sample AI Call Script</p>
                <p className="text-[#4a5568] text-xs">{tabs.find(t => t.id === activeTab)?.label}</p>
              </div>
              <div className="ml-auto flex items-center gap-1.5 bg-green-50 border border-green-100 rounded-full px-3 py-1">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                <span className="text-green-600 text-xs font-semibold">Live</span>
              </div>
            </div>

            {/* Waveform visual */}
            <div className="flex items-center gap-0.5 mb-4 h-8">
              {Array.from({ length: 40 }, (_, i) => (
                <div
                  key={i}
                  className="w-1 rounded-full bg-secondary/30"
                  style={{ height: `${20 + Math.sin(i * 0.7) * 14 + Math.cos(i * 1.3) * 8}%` }}
                />
              ))}
            </div>

            {/* Label above script */}
            <p className="text-xs font-semibold text-navy uppercase tracking-wider mb-2">What Agents Actually Hear</p>

            <blockquote className="text-[#4a5568] text-sm leading-relaxed italic border-l-4 pl-4 mb-4" style={{ borderColor: "#f1663a" }}>
              {c.script}
            </blockquote>

            <p className="text-xs text-[#4a5568] leading-relaxed mb-5">
              This isn&apos;t a cold call — it&apos;s a relevant, timely conversation tied to a real transaction.
            </p>

            <div className="flex items-center gap-4 pt-4 border-t border-gray-100">
              <div className="flex items-center gap-2">
                <i className="ri-time-line text-[#4a5568] text-xs" />
                <span className="text-[#4a5568] text-xs">~45 seconds</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom closing line */}
        <div className="mt-8 md:mt-14 flex items-start gap-4 bg-white border border-gray-100 rounded-2xl px-5 md:px-7 py-4 md:py-5 max-w-3xl mx-auto">
          <span className="w-9 h-9 flex items-center justify-center bg-orange-50 rounded-lg shrink-0">
            <i className="ri-trophy-line text-primary text-base" />
          </span>
          <p className="text-navy text-sm font-medium leading-relaxed">
            Every conversation is an opportunity to secure the buyer side of the deal — before another title company is even in the picture.
          </p>
        </div>
      </div>
    </section>
  );
}
